import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { DateFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { closeDialogAction } from 'store/global/actions';

import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';

import {
  buildRecipientDatesPayload,
  getInitialRecipientDatesById,
  getOriginallySignedRecipientIds,
  isAllRecipientDatesValid,
} from './EditGeneratedOutsideDatesDialog.utils';

const EditGeneratedOutsideDatesDialog = ({ recipients, onSubmit }) => {
  const dispatch = useDispatch();

  const [recipientDatesById, setRecipientDatesById] = useState(() =>
    getInitialRecipientDatesById(recipients),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const originallySignedIds = useMemo(
    () => getOriginallySignedRecipientIds(recipients),
    [recipients],
  );

  const isValid = isAllRecipientDatesValid(recipientDatesById, originallySignedIds);
  const showRecipientNames = recipients.length > 1;

  const onChangeSentDate = useCallback(
    (value, recipientRequestId) =>
      setRecipientDatesById(prev => ({
        ...prev,
        [recipientRequestId]: { ...prev[recipientRequestId], sentDate: value },
      })),
    [],
  );

  const onChangeESignedDate = useCallback(
    (value, recipientRequestId) =>
      setRecipientDatesById(prev => ({
        ...prev,
        [recipientRequestId]: { ...prev[recipientRequestId], eSignedDate: value },
      })),
    [],
  );

  const onClose = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  const onConfirm = useCallback(async () => {
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      const succeeded = await onSubmit(buildRecipientDatesPayload(recipientDatesById));

      if (succeeded) {
        dispatch(closeDialogAction());
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, isValid, onSubmit, recipientDatesById]);

  return (
    <Box className="flex flex-col items-stretch gap-16">
      <Typography color="text.secondary" variant="regular14">
        Correct the Sent and E-Signed dates recorded for this generated-outside delivery.
      </Typography>
      {recipients.map(({ recipientRequestId, signerFullName }) => (
        <Box className="flex flex-col gap-8" key={recipientRequestId}>
          {showRecipientNames && <Typography variant="medium14">{signerFullName}</Typography>}
          <Box className="flex gap-12">
            <DateFieldCDS
              dataTestId={DisclosureDeliveryTestIds.getEditDatesSentInputId(recipientRequestId)}
              label="Sent Date"
              name={recipientRequestId}
              value={recipientDatesById[recipientRequestId]?.sentDate ?? null}
              onChange={onChangeSentDate}
            />
            <DateFieldCDS
              dataTestId={DisclosureDeliveryTestIds.getEditDatesSignedInputId(recipientRequestId)}
              label="E-Signed Date"
              name={recipientRequestId}
              value={recipientDatesById[recipientRequestId]?.eSignedDate ?? null}
              onChange={onChangeESignedDate}
            />
          </Box>
        </Box>
      ))}
      <Box className="pt-8 self-end flex gap-8">
        <Button
          data-testid={DisclosureDeliveryTestIds.EDIT_DATES_CANCEL_BUTTON}
          disabled={isSubmitting}
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid={DisclosureDeliveryTestIds.EDIT_DATES_SUBMIT_BUTTON}
          disabled={!isValid || isSubmitting}
          variant="contained"
          onClick={onConfirm}
        >
          Save dates
        </Button>
      </Box>
    </Box>
  );
};

EditGeneratedOutsideDatesDialog.propTypes = {
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      recipientRequestId: PropTypes.string.isRequired,
      signerFullName: PropTypes.string,
      sentDate: PropTypes.string,
      eSignedDate: PropTypes.string,
    }),
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default EditGeneratedOutsideDatesDialog;
